import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { differenceInCalendarDays } from 'date-fns';
import { Camera20Regular, Edit16Regular } from '@fluentui/react-icons';
import { toast } from 'react-toastify';
import {
    BUSINESS_MODEL_OPTIONS,
    COMPANY_SIZE_OPTIONS,
    CUSTOMER_SEGMENT_OPTIONS,
    SECTOR_OPTIONS,
} from '../NewProjectFlow/CreateProjectStep3';
import { PatchProjectData } from '../../redux/projectDataSlice';
import { GetEmailPreferences, UpdateProjectEmailPreferences } from '../../redux/usersSlice';
import { resolveWorkspaceCustomerStageOptions, resolveWorkspaceScopeAreaOptions } from '../../utils/projectMetadataOptions';
import { resolveWorkspaceProjectTypeSelectOptions } from '../../utils/projectTypes';
import { linkedInProfileName } from '../../utils/linkedInProfileName';
import styles from './ProjectInformation.module.css';
import {
    CURRENCIES,
    LEADERSHIP_ROLES,
    PUBLIC_PROFILES,
    SECTION,
    TECHNOLOGY_LINKS,
    buildForm,
    buildSaveRequest,
    saveError,
    displayValue,
    formatDisplayDate,
    hasValue,
    isHttpUrl,
    parseProjectDate,
    projectLogoSrc,
} from './projectInformationForm';

const withCurrent = (options, current) => {
    if (current && !options.includes(current)) return [...options, current];
    return options;
};

const ProjectInformationPage = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { selectedProject } = useSelector(state => state.projectData);
    const { memberships, activeWorkspaceId, emailPreferences } = useSelector(state => state.user);
    const [mode, setMode] = useState('view');
    const [section, setSection] = useState(null);
    const [form, setForm] = useState(() => buildForm(selectedProject, emailPreferences));
    const [formError, setFormError] = useState('');
    const [saving, setSaving] = useState(false);
    const [logoFailed, setLogoFailed] = useState(false);
    const [logoPreview, setLogoPreview] = useState('');
    const [configOpen, setConfigOpen] = useState(true);
    const emailTouched = useRef(false);

    const workspaceId = activeWorkspaceId || localStorage.getItem('active_workspace_id');
    const activeTenant = useMemo(() => {
        const membership = (memberships || []).find(item => item?.workspace?.id === workspaceId);
        return membership?.workspace || null;
    }, [memberships, workspaceId]);

    const projectTypeOptions = useMemo(
        () => resolveWorkspaceProjectTypeSelectOptions(activeTenant, { includeValue: form.project_type }),
        [activeTenant, form.project_type],
    );
    const companyStageOptions = useMemo(
        () => resolveWorkspaceCustomerStageOptions(activeTenant, { includeValue: form.customer_stage }),
        [activeTenant, form.customer_stage],
    );
    const scopeOptions = useMemo(
        () => resolveWorkspaceScopeAreaOptions(activeTenant, { includeValues: form.scope_areas }),
        [activeTenant, form.scope_areas],
    );

    useEffect(() => {
        dispatch(GetEmailPreferences());
    }, [dispatch]);

    useEffect(() => {
        const savedLogo = projectLogoSrc(selectedProject);
        if (!savedLogo) return;
        setLogoFailed(false);
        setLogoPreview(current => {
            if (current && current !== savedLogo) URL.revokeObjectURL(current);
            return '';
        });
    }, [selectedProject?.base_logo, selectedProject?.logo, selectedProject?.id]);

    useEffect(() => {
        const next = buildForm(selectedProject, emailPreferences);
        setForm(current => {
            if (mode === 'view') {
                emailTouched.current = false;
                return next;
            }
            if (emailTouched.current) return current;
            return {
                ...current,
                projectUpdateEmails: next.projectUpdateEmails,
                reviewReminderEmails: next.reviewReminderEmails,
                emailOriginal: next.emailOriginal,
            };
        });
    }, [selectedProject, emailPreferences, mode]);

    const openEdit = nextSection => {
        setFormError('');
        setSection(nextSection);
        setMode('edit');
        document.querySelector('[class*="viewHeight"]')?.scrollTo?.({ top: 0 });
    };

    const cancelEdit = () => {
        setFormError('');
        setSection(null);
        setMode('view');
    };

    const updateField = (name, value) => {
        setForm(current => ({ ...current, [name]: value }));
    };

    const toggleScope = value => {
        setForm(current => {
            const selected = current.scope_areas.includes(value);
            return {
                ...current,
                scope_areas: selected ? current.scope_areas.filter(item => item !== value) : [...current.scope_areas, value],
            };
        });
    };

    const save = async event => {
        event.preventDefault();
        if (!selectedProject?.id || saving) return;
        const validationError = saveError(section, form);
        if (validationError) {
            setFormError(validationError);
            return;
        }
        setFormError('');
        const request = buildSaveRequest(section, form);
        setSaving(true);
        try {
            if (Object.keys(request.data).length) {
                try {
                    await dispatch(PatchProjectData({ data: request.data, id: selectedProject.id })).unwrap();
                } catch {
                    return;
                }
            }
            if (Object.keys(request.email).length) {
                try {
                    await dispatch(
                        UpdateProjectEmailPreferences({
                            projectId: selectedProject.id,
                            preferences: request.email,
                        }),
                    ).unwrap();
                } catch {
                    setFormError('Email preferences could not be saved.');
                    return;
                }
            }
            setSection(null);
            setMode('view');
        } finally {
            setSaving(false);
        }
    };

    const logoSrc = logoPreview || (!logoFailed && projectLogoSrc(selectedProject)) || '';

    const handleLogoFile = async event => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file || !selectedProject?.id) return;
        const validTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp', 'image/svg+xml'];
        const isSvg = file.name?.toLowerCase().endsWith('.svg');
        if (!validTypes.includes(file.type) && !(isSvg && (!file.type || file.type === 'image/svg+xml'))) {
            toast.error('Use a PNG, JPG, WEBP, or SVG logo.');
            return;
        }
        const preview = URL.createObjectURL(file);
        setLogoFailed(false);
        setLogoPreview(current => {
            if (current) URL.revokeObjectURL(current);
            return preview;
        });
        const data = new FormData();
        data.append('logo', file);
        try {
            await dispatch(PatchProjectData({ data, id: selectedProject.id })).unwrap();
        } catch {
            setLogoPreview(current => {
                if (current) URL.revokeObjectURL(current);
                return '';
            });
        }
    };
    const editing = mode === 'edit';
    const showSection = key => !section || section === key;

    return (
        <div className={styles.page}>
            <div className={styles.inner}>
                {editing ? null : (
                    <button type="button" className={styles.backLink} onClick={() => navigate('/overview')}>
                        ← Back to Project Overview
                    </button>
                )}
                <header className={styles.header}>
                    <div className={styles.identity}>
                        <ProjectLogo src={logoSrc} onError={() => setLogoFailed(true)} onSelect={handleLogoFile} />
                        <div>
                            <h1 className={styles.title}>{editing ? 'Edit Project Information' : 'Project Information'}</h1>
                            <p className={styles.subtitle}>
                                {editing
                                    ? 'Update the confirmed project and organisation context used across Lens.'
                                    : `${displayValue(selectedProject?.project_name)} · Project and organisation context used across Lens.`}
                            </p>
                        </div>
                    </div>
                    <div className={styles.headerActions}>
                        {editing ? (
                            <>
                                <button type="button" className={styles.cancelButton} onClick={cancelEdit} disabled={saving}>
                                    Cancel
                                </button>
                                <button type="submit" form="project-information-form" className={styles.primaryButton} disabled={saving}>
                                    {saving ? 'Saving…' : 'Save changes'}
                                </button>
                            </>
                        ) : (
                            <button type="button" className={styles.primaryButton} onClick={() => openEdit(null)}>
                                Edit project information
                            </button>
                        )}
                    </div>
                </header>
                {formError ? <p className={styles.formError}>{formError}</p> : null}

                {editing ? (
                    <form id="project-information-form" className={styles.stack} onSubmit={save}>
                        {showSection(SECTION.summary) ? <SummaryFields form={form} projectTypeOptions={projectTypeOptions} onChange={updateField} /> : null}
                        {showSection(SECTION.organisation) ? (
                            <OrganisationFields form={form} companyStageOptions={companyStageOptions} onChange={updateField} />
                        ) : null}
                        {showSection(SECTION.engagement) ? <EngagementFields form={form} scopeOptions={scopeOptions} onChange={updateField} onToggleScope={toggleScope} /> : null}
                        {showSection(SECTION.publicProfiles) ? <PublicProfileFields form={form} onChange={updateField} /> : null}
                        {showSection(SECTION.leadership) ? <LeadershipFields form={form} onChange={setForm} /> : null}
                        {showSection(SECTION.technology) ? <TechnologyFields form={form} onChange={updateField} /> : null}
                        {showSection(SECTION.competitors) ? <CompetitorFields form={form} onChange={setForm} /> : null}
                        {showSection(SECTION.configuration) ? (
                            <ConfigurationFields
                                form={form}
                                onEmailChange={(name, value) => {
                                    emailTouched.current = true;
                                    updateField(name, value);
                                }}
                            />
                        ) : null}
                    </form>
                ) : (
                    <ViewBody
                        project={selectedProject}
                        form={form}
                        configOpen={configOpen}
                        onToggleConfig={() => setConfigOpen(open => !open)}
                        onEdit={openEdit}
                        onViewMilestones={() => navigate('/EditProject', { state: { menu: 'Milestones' } })}
                    />
                )}
            </div>
        </div>
    );
};

const ProjectLogo = ({ src, onError, onSelect }) => {
    const inputRef = useRef(null);
    const hasLogo = Boolean(src);

    return (
        <>
            <button
                type="button"
                className={hasLogo ? styles.logoButton : styles.logoButtonEmpty}
                onClick={() => inputRef.current?.click()}
                aria-label={hasLogo ? 'Edit project logo' : 'Add project logo'}
            >
                {hasLogo ? <img src={src} alt="" onError={onError} /> : <Camera20Regular className={styles.logoCamera} />}
                <span className={styles.logoEdit} aria-hidden="true">
                    <Edit16Regular />
                </span>
            </button>
            <input
                ref={inputRef}
                className={styles.logoFileInput}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/svg+xml"
                onChange={onSelect}
            />
        </>
    );
};

const Card = ({ title, onEdit, children }) => (
    <section className={styles.card}>
        <div className={styles.cardHeader}>
            <h2 className={styles.cardTitle}>{title}</h2>
            {onEdit ? (
                <button type="button" className={styles.cardEdit} onClick={onEdit}>
                    Edit
                </button>
            ) : null}
        </div>
        {children}
    </section>
);

const SourceMark = () => (
    <button type="button" className={styles.sourceMark} title="Stored on the project record">
        ⓘ
    </button>
);

const ReadValue = ({ label, value, source }) => (
    <div>
        <p className={styles.valueLabel}>
            {label}
            {source ? <SourceMark /> : null}
        </p>
        <p className={styles.valueText}>{displayValue(value)}</p>
    </div>
);

const Field = ({ label, required, children, full }) => (
    <label className={full ? styles.fieldFull : styles.field}>
        <span className={styles.fieldLabel}>
            {label}
            {required ? <span className={styles.required}> *</span> : null}
        </span>
        {children}
    </label>
);

const TextInput = props => <input className={styles.input} {...props} />;

const SelectInput = ({ className, children, ...props }) => (
    <select className={className ? `${styles.select} ${className}` : styles.select} {...props}>
        {children}
    </select>
);

const ProfileLink = ({ href, children }) => {
    if (!isHttpUrl(href)) return <span className={styles.profileChip}>{children}</span>;
    return (
        <a className={styles.profileChip} href={href} target="_blank" rel="noreferrer">
            {children}
        </a>
    );
};

const SECOND_LEVEL_SUFFIXES = new Set(['co', 'com', 'org', 'net', 'ac', 'gov']);

function competitorLabel(value) {
    const text = String(value || '').trim();
    if (!text) return '';
    const withProtocol = /^https?:\/\//i.test(text) ? text : `https://${text}`;
    let hostname = '';
    try {
        hostname = new URL(withProtocol).hostname.replace(/^www\./i, '');
    } catch {
        return text;
    }
    const labels = hostname.split('.').filter(Boolean);
    if (labels.length < 2) return text;
    const beforeTld = labels.length >= 3 && SECOND_LEVEL_SUFFIXES.has(labels[labels.length - 2].toLowerCase()) ? labels[labels.length - 3] : labels[labels.length - 2];
    if (!beforeTld) return text;
    return beforeTld.charAt(0).toUpperCase() + beforeTld.slice(1);
}

const ViewBody = ({ project, form, configOpen, onToggleConfig, onEdit, onViewMilestones }) => {
    const milestones = [...(project?.project_milestones || [])]
        .filter(item => item?.milestoneName || item?.milestoneDate)
        .sort((left, right) => {
            const leftTime = parseProjectDate(left.milestoneDate)?.getTime() || 0;
            const rightTime = parseProjectDate(right.milestoneDate)?.getTime() || 0;
            return leftTime - rightTime;
        });
    const nextIndex = milestones.findIndex(item => {
        const date = parseProjectDate(item.milestoneDate);
        return date && differenceInCalendarDays(date, new Date()) >= 0;
    });
    const visibleMilestones = nextIndex === -1 ? milestones.slice(-3) : milestones.slice(nextIndex, nextIndex + 3);
    const publicLinks = PUBLIC_PROFILES.filter(item => hasValue(form[item.key]));
    const technologyLinks = TECHNOLOGY_LINKS.filter(item => hasValue(form[item.key]));
    const competitorLinks = form.competitors.filter(hasValue);

    return (
        <>
            <div className={styles.columns}>
                <Card title="Project Summary" onEdit={() => onEdit(SECTION.summary)}>
                    <div className={styles.values}>
                        <ReadValue label="Project name" value={form.project_name} source />
                        <ReadValue label="Organisation" value={form.company_name} source />
                        <ReadValue label="Project type" value={form.project_type} />
                        <ReadValue label="Status" value={form.status} />
                        <ReadValue label="Investment type" value={form.investment_type} />
                        <ReadValue label="Deadline" value={formatDisplayDate(form.project_deadline)} />
                    </div>
                </Card>
                <Card title="Organisation" onEdit={() => onEdit(SECTION.organisation)}>
                    <div className={styles.values}>
                        <ReadValue label="Sector" value={form.sector} source />
                        <ReadValue label="Company stage" value={form.customer_stage} source />
                        <ReadValue label="Company size" value={form.company_size} />
                        <ReadValue label="Year established" value={form.established_year} source />
                        <ReadValue label="Business model" value={form.customer_type} />
                        <ReadValue label="Customer segment" value={form.customer_segment} />
                    </div>
                    <div className={styles.descriptionBlock}>
                        <p className={styles.valueLabel}>Business Description</p>
                        <p className={styles.valueText}>{displayValue(form.description)}</p>
                    </div>
                </Card>
                <Card title="Engagement & Scope" onEdit={() => onEdit(SECTION.engagement)}>
                    <p className={`${styles.valueLabel} ${styles.milestoneCount}`}>Engagement objective</p>
                    <p className={styles.objective}>{displayValue(form.engagement_objective)}</p>
                    {form.scope_areas.length ? (
                        <div className={styles.chips}>
                            {form.scope_areas.map(area => (
                                <span key={area} className={styles.scopeChip}>
                                    {area}
                                </span>
                            ))}
                        </div>
                    ) : (
                        <p className={styles.emptyText}>No scope areas selected.</p>
                    )}
                </Card>
                <Card title="Milestones">
                    <p className={`${styles.valueTextStrong} ${styles.milestoneCount}`}>
                        {milestones.length} milestone{milestones.length === 1 ? '' : 's'}
                    </p>
                    {visibleMilestones.map((item, index) => {
                        const date = parseProjectDate(item.milestoneDate);
                        const isUpcoming = index === 0 && date && differenceInCalendarDays(date, new Date()) >= 0;
                        return (
                            <div key={`${item.milestoneName}-${item.milestoneDate}-${index}`} className={styles.milestoneRow}>
                                <div>
                                    <p className={styles.milestoneName}>{item.milestoneName || '—'}</p>
                                    {isUpcoming ? <p className={styles.milestoneUpcoming}>Upcoming</p> : null}
                                </div>
                                <p className={styles.milestoneDate}>{formatDisplayDate(item.milestoneDate)}</p>
                            </div>
                        );
                    })}
                    <button type="button" className={`${styles.cardEdit} ${styles.viewAll}`} onClick={onViewMilestones}>
                        View all milestones →
                    </button>
                </Card>
                <Card title="Public Profiles" onEdit={() => onEdit(SECTION.publicProfiles)}>
                    {publicLinks.length ? (
                        <div className={styles.chips}>
                            {publicLinks.map(item => (
                                <ProfileLink key={item.key} href={form[item.key]}>
                                    {item.label} ↗
                                </ProfileLink>
                            ))}
                        </div>
                    ) : (
                        <p className={styles.emptyText}>No public profiles added.</p>
                    )}
                </Card>
                <Card title="Leadership" onEdit={() => onEdit(SECTION.leadership)}>
                    {form.profiles.length ? (
                        form.profiles.map(profile => {
                            const role = LEADERSHIP_ROLES.find(item => item.key === profile.role);
                            const personName = linkedInProfileName(profile.url);
                            return (
                                <div key={profile.role} className={styles.leaderRow}>
                                    <div>
                                        <p className={styles.leaderRole}>{role?.label || profile.role}</p>
                                        {personName ? <p className={styles.valueTextStrong}>{personName}</p> : null}
                                        {!personName && !isHttpUrl(profile.url) ? <p className={styles.valueTextStrong}>{profile.url}</p> : null}
                                    </div>
                                    {isHttpUrl(profile.url) ? <ProfileLink href={profile.url}>LinkedIn ↗</ProfileLink> : null}
                                </div>
                            );
                        })
                    ) : (
                        <p className={styles.emptyText}>No leadership profiles added.</p>
                    )}
                </Card>
                <Card title="Technology" onEdit={() => onEdit(SECTION.technology)}>
                    {technologyLinks.length ? (
                        <div className={styles.chips}>
                            {technologyLinks.map(item => (
                                <ProfileLink key={item.key} href={form[item.key]}>
                                    {item.label} ↗
                                </ProfileLink>
                            ))}
                        </div>
                    ) : (
                        <p className={styles.emptyText}>No technology profiles added.</p>
                    )}
                </Card>
                <Card title="Competitors" onEdit={() => onEdit(SECTION.competitors)}>
                    {competitorLinks.length ? (
                        <div className={styles.chips}>
                            {competitorLinks.map((item, index) => (
                                <ProfileLink key={`${item}-${index}`} href={item}>
                                    {competitorLabel(item)} ↗
                                </ProfileLink>
                            ))}
                        </div>
                    ) : (
                        <p className={styles.emptyText}>No competitors added.</p>
                    )}
                </Card>
            </div>
            <section className={`${styles.configCard} ${styles.stack}`}>
                <button type="button" className={styles.configHeader} onClick={onToggleConfig} aria-expanded={configOpen}>
                    <span>
                        <span className={styles.cardTitle}>Project Configuration</span>
                        <span className={styles.configHint}>Email preferences</span>
                    </span>
                    <span className={styles.configToggle}>{configOpen ? '−' : '+'}</span>
                </button>
                {configOpen ? (
                    <div className={styles.configBody}>
                        <button type="button" className={styles.inlineLink} onClick={() => onEdit(SECTION.configuration)}>
                            Edit configuration →
                        </button>
                    </div>
                ) : null}
            </section>
            <p className={styles.updated}>Last updated {formatDisplayDate(project?.project_modified)}</p>
        </>
    );
};

const SummaryFields = ({ form, projectTypeOptions, onChange }) => (
    <section className={styles.card}>
        <h2 className={styles.cardTitle}>Project Summary</h2>
        <div className={styles.fields}>
            <Field label="Project Name" required>
                <TextInput value={form.project_name} maxLength={100} onChange={event => onChange('project_name', event.target.value)} />
            </Field>
            <Field label="Organisation Name">
                <TextInput value={form.company_name} maxLength={100} onChange={event => onChange('company_name', event.target.value)} />
            </Field>
            <Field label="Project Type" required>
                <SelectInput value={form.project_type} onChange={event => onChange('project_type', event.target.value)}>
                    <option value="">Select</option>
                    {projectTypeOptions.map(option => (
                        <option key={option.value} value={option.value}>
                            {option.text}
                        </option>
                    ))}
                </SelectInput>
            </Field>
            <Field label="Project Deadline" required>
                <TextInput type="date" value={form.project_deadline} onChange={event => onChange('project_deadline', event.target.value)} />
            </Field>
        </div>
    </section>
);

const OrganisationFields = ({ form, companyStageOptions, onChange }) => (
    <section className={styles.card}>
        <h2 className={styles.cardTitle}>Organisation</h2>
        <div className={styles.fields}>
            <Field label="Sector / Industry">
                <SelectInput value={form.sector} onChange={event => onChange('sector', event.target.value)}>
                    <option value="">Select</option>
                    {withCurrent(SECTOR_OPTIONS, form.sector).map(option => (
                        <option key={option} value={option}>
                            {option}
                        </option>
                    ))}
                </SelectInput>
            </Field>
            <Field label="Company Stage">
                <SelectInput value={form.customer_stage} onChange={event => onChange('customer_stage', event.target.value)}>
                    <option value="">Select</option>
                    {companyStageOptions.map(option => (
                        <option key={option.value} value={option.value}>
                            {option.text}
                        </option>
                    ))}
                </SelectInput>
            </Field>
            <Field label="Company Size">
                <SelectInput value={form.company_size} onChange={event => onChange('company_size', event.target.value)}>
                    <option value="">Select</option>
                    {withCurrent(COMPANY_SIZE_OPTIONS, form.company_size).map(option => (
                        <option key={option} value={option}>
                            {option}
                        </option>
                    ))}
                </SelectInput>
            </Field>
            <Field label="Year Established">
                <TextInput value={form.established_year} inputMode="numeric" onChange={event => onChange('established_year', event.target.value)} />
            </Field>
            <Field label="Business Model">
                <SelectInput value={form.customer_type} onChange={event => onChange('customer_type', event.target.value)}>
                    <option value="">Select</option>
                    {withCurrent(BUSINESS_MODEL_OPTIONS, form.customer_type).map(option => (
                        <option key={option} value={option}>
                            {option}
                        </option>
                    ))}
                </SelectInput>
            </Field>
            <Field label="Primary Customer Segment">
                <SelectInput value={form.customer_segment} onChange={event => onChange('customer_segment', event.target.value)}>
                    <option value="">Select</option>
                    {withCurrent(CUSTOMER_SEGMENT_OPTIONS, form.customer_segment).map(option => (
                        <option key={option} value={option}>
                            {option}
                        </option>
                    ))}
                </SelectInput>
            </Field>
            <Field label="Revenue">
                <span className={styles.revenueRow}>
                    <SelectInput className={styles.currencySelect} value={form.currency} onChange={event => onChange('currency', event.target.value)}>
                        {CURRENCIES.map(option => (
                            <option key={option.code} value={option.code}>
                                {option.code}
                            </option>
                        ))}
                    </SelectInput>
                    <TextInput value={form.revenue} inputMode="decimal" onChange={event => onChange('revenue', event.target.value)} />
                </span>
            </Field>
            <Field label="Companies House ID">
                <TextInput value={form.companieshouse} maxLength={20} onChange={event => onChange('companieshouse', event.target.value)} />
            </Field>
            <Field label="Business Description" full>
                <textarea className={styles.textarea} value={form.description} onChange={event => onChange('description', event.target.value)} />
            </Field>
        </div>
    </section>
);

const EngagementFields = ({ form, scopeOptions, onChange, onToggleScope }) => (
    <section className={styles.card}>
        <h2 className={styles.cardTitle}>Engagement & Scope</h2>
        <div className={styles.fields}>
            <Field label="Engagement Objective" full>
                <textarea
                    className={styles.textarea}
                    value={form.engagement_objective}
                    onChange={event => onChange('engagement_objective', event.target.value)}
                />
            </Field>
        </div>
        <p className={`${styles.fieldLabel} ${styles.groupLabel}`}>Scope Areas</p>
        <div className={styles.scopeEditor}>
            {scopeOptions.map(option => {
                const selected = form.scope_areas.includes(option.value);
                return (
                    <button
                        key={option.value}
                        type="button"
                        className={selected ? `${styles.scopeToggle} ${styles.scopeToggleSelected}` : styles.scopeToggle}
                        aria-pressed={selected}
                        onClick={() => onToggleScope(option.value)}
                    >
                        {option.text}
                    </button>
                );
            })}
        </div>
    </section>
);

const PublicProfileFields = ({ form, onChange }) => (
    <section className={styles.card}>
        <h2 className={styles.cardTitle}>Public Profiles</h2>
        <div className={styles.fields}>
            <Field label="Primary Website">
                <TextInput value={form.website} placeholder="https://" maxLength={100} onChange={event => onChange('website', event.target.value)} />
            </Field>
            <Field label="Product Website">
                <TextInput
                    value={form.product_website}
                    placeholder="https://"
                    maxLength={100}
                    onChange={event => onChange('product_website', event.target.value)}
                />
            </Field>
            {PUBLIC_PROFILES.map(item => (
                <Field key={item.key} label={item.label}>
                    <TextInput
                        value={form[item.key]}
                        placeholder="https://"
                        maxLength={200}
                        onChange={event => onChange(item.key, event.target.value)}
                    />
                </Field>
            ))}
        </div>
    </section>
);

const LeadershipFields = ({ form, onChange }) => {
    const usedRoles = new Set(form.profiles.map(profile => profile.role));
    const addProfile = () => {
        const next = LEADERSHIP_ROLES.find(role => !usedRoles.has(role.key));
        if (!next) return;
        onChange(current => ({ ...current, profiles: [...current.profiles, { role: next.key, url: '' }] }));
    };
    const updateProfile = (index, patch) => {
        onChange(current => ({
            ...current,
            profiles: current.profiles.map((profile, profileIndex) => (profileIndex === index ? { ...profile, ...patch } : profile)),
        }));
    };

    return (
        <section className={styles.card}>
            <h2 className={styles.cardTitle}>Leadership</h2>
            {form.profiles.map((profile, index) => (
                <div key={`${profile.role}-${index}`} className={styles.profileEditor}>
                    <Field label="Role">
                        <SelectInput value={profile.role} onChange={event => updateProfile(index, { role: event.target.value })}>
                            {LEADERSHIP_ROLES.filter(role => role.key === profile.role || !usedRoles.has(role.key)).map(role => (
                                <option key={role.key} value={role.key}>
                                    {role.label}
                                </option>
                            ))}
                        </SelectInput>
                    </Field>
                    <Field label="LinkedIn">
                        <TextInput
                            value={profile.url}
                            placeholder="https://"
                            maxLength={200}
                            onChange={event => updateProfile(index, { url: event.target.value })}
                        />
                    </Field>
                    <button
                        type="button"
                        className={styles.removeLink}
                        onClick={() => onChange(current => ({ ...current, profiles: current.profiles.filter((_, profileIndex) => profileIndex !== index) }))}
                    >
                        Remove
                    </button>
                </div>
            ))}
            {form.profiles.length < LEADERSHIP_ROLES.length ? (
                <button type="button" className={`${styles.textLink} ${styles.addRow}`} onClick={addProfile}>
                    + Add profile
                </button>
            ) : null}
        </section>
    );
};

const TechnologyFields = ({ form, onChange }) => (
    <section className={styles.card}>
        <h2 className={styles.cardTitle}>Technology</h2>
        <div className={styles.urlStack}>
            {TECHNOLOGY_LINKS.map(item => (
                <Field key={item.key} label={item.label === 'Similarweb' ? 'SimilarWeb' : item.label}>
                    <TextInput
                        value={form[item.key]}
                        placeholder="https://"
                        maxLength={200}
                        onChange={event => onChange(item.key, event.target.value)}
                    />
                </Field>
            ))}
        </div>
    </section>
);

const CompetitorFields = ({ form, onChange }) => (
    <section className={styles.card}>
        <h2 className={styles.cardTitle}>Competitors</h2>
        {form.competitors.map((competitor, index) => (
            <div key={`competitor-${index}`} className={styles.competitorRow}>
                <Field label="Competitor">
                    <TextInput
                        value={competitor}
                        maxLength={100}
                        onChange={event =>
                            onChange(current => ({
                                ...current,
                                competitors: current.competitors.map((item, itemIndex) => (itemIndex === index ? event.target.value : item)),
                            }))
                        }
                    />
                </Field>
                <button
                    type="button"
                    className={styles.removeLink}
                    onClick={() => onChange(current => ({ ...current, competitors: current.competitors.filter((_, itemIndex) => itemIndex !== index) }))}
                >
                    Remove
                </button>
            </div>
        ))}
        <button
            type="button"
            className={`${styles.textLink} ${styles.addRow}`}
            onClick={() => onChange(current => ({ ...current, competitors: [...current.competitors, ''] }))}
        >
            + Add competitor
        </button>
    </section>
);

const ConfigurationFields = ({ form, onEmailChange }) => (
    <section className={styles.card}>
        <h2 className={styles.cardTitle}>Project Configuration</h2>
        <div className={styles.checkList}>
            <label className={styles.check}>
                <input
                    type="checkbox"
                    checked={form.projectUpdateEmails}
                    onChange={event => onEmailChange('projectUpdateEmails', event.target.checked)}
                />
                Project update emails
            </label>
            <label className={styles.check}>
                <input
                    type="checkbox"
                    checked={form.reviewReminderEmails}
                    onChange={event => onEmailChange('reviewReminderEmails', event.target.checked)}
                />
                Review reminder emails
            </label>
        </div>
    </section>
);

export default ProjectInformationPage;
